import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  CssBaseline,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Site, createSite } from "../models/Site";
import { PageContext } from "../contexts/PageContext";
import PageHeader from "../components/PageHeader";
import Minibar from "../components/Minibar";
import { Save, Delete, LibraryAdd, LayersClear } from "@mui/icons-material";
import {
  deleteBlob,
  deleteRecord,
  loadOrgModel,
  saveRecord,
} from "../api/connection";
import {
  showConfirmDialog,
  showProgressDialog,
  showSnackbar,
} from "../components/UniversalDialog";
import { deleteTiles, generateTiles } from "../utils/googleMaps";

const EditSitePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [site, setSite] = useState<Site | null>(null);
  const [originalSite, setOriginalSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isLoggedIn, user } = useContext(PageContext);

  const [errors, setErrors] = useState({
    name: "",
    // simId: "",
    location: "",
    gridSize: "",
    cellSize: "",
  });

  useEffect(() => {
    async function fetchSite(id: string) {
      try {
        if (id === "new") {
          setSite({
            _id: "",
            orgId: user!.orgId,
            name: "",
            simId: "",
            location: "",
            gridSize: 0,
            cellSize: 0,
            elevation: 0,
            numberOfTiles: 0,
          });
        } else {
          const site = createSite(id);
          await loadOrgModel(site);
          setSite(site);
          setOriginalSite(site);
        }
      } catch (err) {
        setError("Failed to fetch site data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    if (id) {
      fetchSite(id);
    }
  }, [id, user]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    updatedSite: Site
  ) => {
    const { name, value } = e.target;
    if (name === "gridSize" || name === "cellSize" || name === "elevation") {
      setSite({ ...updatedSite, [name]: parseInt(value) });
      return;
    }
    setSite({ ...updatedSite, [name]: value });
  };

  const saveSite = async (record: Site) => {
    setLoading(true);
    if (!validateFields()) {
      setLoading(false);
      return; // Stop if validation fails
    }

    try {
      console.log("Saving Site:", record);
      const res = await saveRecord<Site>("sites", record);

      if (
        !originalSite ||
        originalSite.location !== record.location ||
        originalSite.gridSize !== record.gridSize ||
        originalSite.cellSize !== record.cellSize
      ) {
        // get elevation data and save/update to azure storage
        const mgrs = record.location;
        // const elevations = await getElevation(
        //   mgrs,
        //   record.gridSize,
        //   record.cellSize
        // );
        // // create a float32array from the elevations
        // const elevationData = new Float32Array(elevations.elevations);
        // // convert to buffer
        // const elevationBuffer = elevationData.buffer;
        // // send the elevation data to azure function to handle saving to azure storage

        // const blobUploadRes = await uploadBlob(
        //   "techmage-elevations",
        //   `${res._id}.bin`,
        //   elevationBuffer
        // );
        // console.log({ blobUploadRes });
        // if ("error" in blobUploadRes) {
        //   throw new Error(blobUploadRes.error);
        // }
      }
      if (
        (originalSite &&
          (originalSite.location !== record.location ||
            originalSite.elevation !== record.elevation ||
            originalSite.gridSize !== record.gridSize)) ||
        !originalSite
      ) {
        const updatedSite = createSite(res._id);
        await loadOrgModel(updatedSite);
        console.log("Updated Site:", updatedSite);
        setSite(updatedSite);
        // open dialog to confirm if user wants to generate new tiles
        // setOpenGenerateTilesDialog(true);
        showConfirmDialog(
          "GENERATE TILES",
          "Do you want to generate new tiles for this site?"
        );
      } else {
        navigate("/sites");
      }
    } catch (err) {
      console.error(`Failed to ${!record._id ? "save" : "update"} site:`, err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSite = async (record: Site) => {
    setLoading(true);
    try {
      const res = await deleteRecord<Site>("sites", record);
      console.log("Delete Response:", res);
      const blobDeleteRes = await deleteBlob(
        "techmage-elevations",
        `${record._id}.bin`
      );
      console.log({ blobDeleteRes });

      const progressDialog = showProgressDialog(
        "Deleting",
        "Deleting tiles...",
        0
      ) as ReturnType<typeof showProgressDialog>;

      const deleteTilesRes = await deleteTiles(
        record,
        progressDialog.setProgress
      );
      progressDialog.onComplete(false, "");
      showSnackbar("Site deleted successfully!", "success");
      navigate("/sites");
    } catch (err) {
      console.error("Failed to delete site:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTiles = async (record: Site) => {
    setLoading(true);
    if (!site) {
      console.warn("No site available.");
      setLoading(false);
      return;
    }

    const progressDialog = showProgressDialog(
      "Deleting Tiles",
      "Deleting tiles from Google Maps...",
      0
    ) as ReturnType<typeof showProgressDialog>;

    try {
      const newSite = await deleteTiles(site, progressDialog.setProgress);
      if ("error" in newSite) throw new Error(String(newSite.error));
      setSite(newSite);
    } catch (err) {
      console.error("Failed to delete tiles:", err);
      setError(`Failed to delete tiles: ${(err as Error).message}`);
    } finally {
      progressDialog.onComplete(false, "");
      setLoading(false);
    }
  };

  const handleGenerateTiles = async (record: Site) => {
    if (!site) {
      console.warn("No site available.");
      return;
    }

    setLoading(true);
    let progressDialog: ReturnType<typeof showProgressDialog> | null = null;
    try {
      const yes = await showConfirmDialog(
        "GENERATE TILES",
        "Are you sure you want to generate new tiles for this site?"
      );
      if (!yes) {
        setLoading(false);
        return;
      }

      progressDialog = showProgressDialog(
        "Tiles Progress",
        "Deleting any existing tiles...",
        0
      ) as ReturnType<typeof showProgressDialog>;

      const deletedSite = await deleteTiles(site, (value) => {
        // console.log(`Delete Tiles Progress: ${value}%`);
        progressDialog!.setProgress(value);
      });
      setSite(deletedSite);
      progressDialog.updateText("Generating new tiles...");
      const newSite = await generateTiles(site, (value) => {
        // console.log(`Generate Tiles Progress: ${value}%`);
        progressDialog!.setProgress(value);
      });
      if ("error" in newSite) {
        throw new Error(String(newSite.error));
      }

      setSite(newSite);
      showSnackbar("Tiles generated successfully!", "success");
      // navigate(`/edit-site/${newSite._id}`);
    } catch (err) {
      console.error("Failed to generate tiles:", err);
      setError(`Failed to generate tiles: ${(err as Error).message}`);
      showSnackbar(
        `Failed to generate tiles: ${(err as Error).message}`,
        "error"
      );
    } finally {
      console.log("Closing progress dialog...");
      if (progressDialog) {
        progressDialog.onComplete(false, "");
      }
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (site) {
      handleChange(e, site);
    }
  };

  const handleDeleteClick = async () => {
    const yes = await showConfirmDialog(
      "CONFIRM DELETION",
      "Are you sure you want to delete this site?"
    );
    if (yes && site) {
      await handleDeleteSite(site);
    }
  };

  // const handleCancel = async () => {
  //   if (!site) {
  //     return;
  //   }
  //   if (originalSite) {
  //     showProgressDialog(
  //       "Deleting Tiles",
  //       "Deleting tiles from Google Maps...",
  //       0
  //     ) as ReturnType<typeof showProgressDialog>;

  //   }
  //   // const newSite = await deleteTiles(site, setProgress, setLoading);
  //   const newSite = await deleteTiles(site, () => {});

  //   // setOpenProgressDialog(false);
  //   setLoading(false);
  //   if ("error" in newSite) {
  //     console.error(newSite.error);
  //     return;
  //   }
  //   setSite(newSite);

  //   // setOpenGenerateTilesDialog(false);
  //   // delete previous tiles
  //   if (!originalSite) {
  //     navigate(`/edit-site/${newSite._id}`);
  //   }
  // };

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  const validateFields = () => {
    const newErrors = {
      name: !site?.name ? "Name is required" : "",
      // simId: !site?.simId ? "Sim ID is required" : "",
      location: !site?.location ? "Location is required" : "",
      gridSize: !site?.gridSize ? "Grid size is required" : "",
      cellSize: !site?.cellSize ? "Cell size is required" : "",
    };

    setErrors(newErrors);

    // If there are no errors, return true
    return Object.values(newErrors).every((error) => error === "");
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        {/* page header */}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            m: 0,
            backgroundColor: "#212121",
            color: "#ECECEC",
            minHeight: "100vh",
          }}
        >
          <Backdrop
            open={loading}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <PageHeader
            title={id === "new" ? "New Site" : "Edit Site Details"}
            rightComponent={[
              <Minibar
                minibarItems={
                  id === "new"
                    ? [
                        {
                          text: `Save New Site`,
                          icon: <Save />,
                          onClick: () => site && saveSite(site),
                        },
                      ]
                    : [
                        {
                          text: `Update Site`,
                          icon: <Save />,
                          onClick: () => site && saveSite(site),
                          disabled: loading,
                        },
                        {
                          text: "Delete Site",
                          icon: <Delete />,
                          onClick: () => handleDeleteClick(),
                          disabled: loading,
                        },
                        {
                          text: "Generate Tiles",
                          icon: <LibraryAdd />,
                          onClick: () => site && handleGenerateTiles(site),
                          disabled: loading,
                        },
                        {
                          text: "Delete Tiles",
                          icon: <LayersClear />,
                          onClick: () => site && handleDeleteTiles(site),
                          disabled: loading || !site?.numberOfTiles,
                        },
                      ]
                }
                barLength={3}
              />,
            ]}
            // breadcrumbElements={[
            //   {
            //     label: "Sites",
            //     href: "/sites",
            //   },
            //   {
            //     label:
            //       site && id !== "new"
            //         ? site.name
            //         : "New Site",
            //     href: "",
            //   },
            // ]}
          />
          <Container maxWidth="xl" sx={{ pt: "2rem" }}>
            <Grid
              container
              spacing={3}
              justifyContent="center"
              alignItems="center"
            >
              <Grid size={12}>
                <Stack spacing={2}>
                  <TextField
                    label="Name"
                    name="name"
                    value={site?.name || ""}
                    fullWidth
                    onChange={handleInputChange}
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                  <TextField
                    label="Sim ID"
                    name="simId"
                    value={site?.simId || ""}
                    fullWidth
                    onChange={handleInputChange}
                    // error={!!errors.simId}
                    // helperText={errors.simId}
                  />
                  <TextField
                    label="Location (MGRS)"
                    name="location"
                    value={site?.location || ""}
                    fullWidth
                    onChange={handleInputChange}
                    error={!!errors.location}
                    helperText={errors.location}
                  />
                  <TextField
                    label="Grid Size"
                    name="gridSize"
                    value={site?.gridSize || ""}
                    fullWidth
                    onChange={handleInputChange}
                    error={!!errors.gridSize}
                    helperText={errors.gridSize}
                  />
                  <TextField
                    label="Cell Size"
                    name="cellSize"
                    value={site?.cellSize || ""}
                    fullWidth
                    onChange={handleInputChange}
                    error={!!errors.cellSize}
                    helperText={errors.cellSize}
                  />
                  <TextField
                    label="Elevation"
                    name="elevation"
                    value={site?.elevation || 0}
                    fullWidth
                    onChange={handleInputChange}
                    onFocus={(e) => e.target.select()}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default EditSitePage;
